import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger.js';
import { ScrollSmoother } from 'gsap/ScrollSmoother.js';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin.js';

export default class Scrolly {
  constructor(element) {
    gsap.registerPlugin(ScrollTrigger, ScrollSmoother, ScrollToPlugin);

    this.element = element;

    this.btnsAnchors = document.querySelectorAll('a[href^="index.html#"]');

    this.setOptions();
    this.init();
  }

  init() {
    this.initScroller();

    this.initPlace();
    this.initProjets();
    this.initCompetences();
    this.initPlus();
    this.initChien();
    this.initInterets();
    this.initMarche();
    this.initContexte();
    this.initWick();
    this.initCroquis();
    this.initResultats();
    this.initAmelioration();
    this.initDiscute();

    // ONE refresh only
    ScrollTrigger.refresh(true);

    // Gsap links scroll
    this.gsapBtn();

    // Gsap links scroll
    const scrollTarget = sessionStorage.getItem('scrollTarget');
    if (scrollTarget) {
      sessionStorage.removeItem('scrollTarget');

      window.addEventListener('load', () => {
        requestAnimationFrame(() => {
          ScrollTrigger.refresh(true);

          gsap.delayedCall(0.1, () => {
            this.smoother.scrollTo(scrollTarget, true);
          });
        });
      });
    }

    // Gsap links scroll
    if (window.location.hash) {
      const hash = window.location.hash;

      window.addEventListener('load', () => {
        // wait one frame AFTER load
        requestAnimationFrame(() => {
          ScrollTrigger.refresh(true);

          // small delay to ensure measurements are correct
          gsap.delayedCall(0.2, () => {
            this.smoother.scrollTo(hash, true);
          });
        });
      });
    }
  }

  // Gsap links scroll
  gsapBtn() {
    this.btnsAnchors.forEach(link => {
      link.addEventListener('click', (e) => {
        const href = link.getAttribute('href');
        
        // 1. Extract the ID correctly (e.g., "index.html#projets" -> "#projets")
        const targetId = href.includes('#') ? href.substring(href.indexOf('#')) : null;
        
        if (targetId) {
          const targetElem = document.querySelector(targetId);

          // 2. Check if the element actually exists on the current page
          if (targetElem) {
            e.preventDefault();
            
            // 3. Pass the actual element or the valid selector string
            this.smoother.scrollTo(targetElem, true, "top top");
          } else {
            // If the element isn't on this page, let the default link behavior 
            // take the user to the new page.
            console.warn(`Target ${targetId} not found on this page.`);
          }
        }
      });
    });
  }




  // -----------------------
  // Smooth Scroll
  // -----------------------
  initScroller() {
    this.smoother = ScrollSmoother.create({
        smooth: 1,
        effects: true,
        smoothTouch: 0.1,
        //ease: "ease-out",
    })
  }

  setOptions(){
    if('skew' in this.element.dataset){
        this.options.hasSkew = true;
        this.initSkew();
    }

    if('pinItems' in this.element.dataset){
        this.options.hasPinItems = true;
        this.initPins();
    }

    if(this.element.querySelector('.js-horiz')){
        this.initHorizontal();
    }
  }

  initHorizontal(){
    const sectionHoriz = this.element.querySelector('.js-horiz'); // Selectionne max 1 section
    const panels = sectionHoriz.querySelectorAll('.js-panel');
    const nbPanels = panels.length - 1;
    const buffer = 200;

    gsap.to(panels, {
        xPercent: -100 * nbPanels,
        ease: "none",
        scrollTrigger: {
            pin: true,
            trigger: sectionHoriz,
            scrub: 1,
            snap: 1 / nbPanels,
            end: () => "+=" + (sectionHoriz.offsetWidth + buffer),
        }
    });
  }









  // -----------------------
  // PLACE
  // -----------------------
  initPlace() {
    if (!this.element.querySelector('.place')) return;

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: '.place',
        start: 'top top',
        end: '+=3000',
        scrub: true,
        pin: true,
      }
    });

    tl.to('.texte_initial_place', { opacity: 1, y: -50 })
      .to('.cercle_jaune_place', { opacity: 1, y: -50, scale: 400 }, '<')
      .to('.cercle_rouge_place', { x: '50vw' })
      .to('.cercle_bleue_place', { x: '-50vw' }, '<')
      .to(['.cercle_rouge_place', '.cercle_bleue_place', '.cercle_jaune_place'], { scale: 0.3 })
      .to(['.cercle_rouge_place', '.cercle_bleue_place'], { rotation: 180 })
      .to('.texte_initial_place', { opacity: 0 }, '<')
      .to('.cercle_jaune_place', { opacity: 0 }, '<')
      .to('.texte_final_place', { opacity: 1, yPercent: -100 }, '<')
      .to('.triangle_jaune_place', { opacity: 1, yPercent: -25, y: -60 }, '<')
      .to('.texte_final_place', { y: -40 })
      .to('.triangle_jaune_place', { rotation: 30, x: 40 })
      .to('.cercle_rouge_place', { xPercent: 15 }, '<')
      .to('.cercle_bleue_place', { xPercent: -15 }, '<');
  }

  // -----------------------
  // PROJETS
  // -----------------------
  initProjets() {
    if (!this.element.querySelector('.projets')) return;

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: '.projets',
        start: 'top 50%',
        end: '60% 60%',
        scrub: true,
      }
    });

    tl.to('.carre_rouge_projets', { top: '18%', opacity: 1 })
      .to('.cercle_bleu_projets', { top: '32%', opacity: 1 }, '<')
      .to('.triangle_jaune_projets', { top: '50%', opacity: 1 }, '<')
      .to('.swiper-pagination', { opacity: 1 })
      .to('.slide-1, .slide-2, .slide-3, .slide-4, .slide-5', {
        marginTop: 0,
        opacity: 1,
        stagger: 0.1
      });
  }

  // -----------------------
  // COMPETENCES
  // -----------------------
  initCompetences() {
    const sections = [
      { trigger: '.competences_titre', target: '.ligne_connection_competences_1' },
      { trigger: '.competence_1', target: '.ligne_connection_competences_2' },
      { trigger: '.competence_2', target: '.ligne_connection_competences_3' },
      { trigger: '.competence_3', target: '.ligne_connection_competences_4' },
    ];

    sections.forEach(({ trigger, target }) => {
      if (!this.element.querySelector(trigger)) return;
      
      gsap.timeline({
        scrollTrigger: {
          trigger,
          start: 'top 80%',
          end: '+=800',
          scrub: true,
        }
      }).to(target, { width: '5vw' }, "<");
    });
  }

  // -----------------------
  // PLUS - SIMPLE FADE
  // -----------------------
  simpleFade(trigger, target, options = {}) {
    if (!this.element.querySelector(trigger)) return;

    gsap.timeline({
      scrollTrigger: {
        trigger,
        start: 'top 50%',
        end: '+=400',
        scrub: true,
        ...options
      }
    }).to(target, { opacity: 1, y: 0 });
  }

  initPlus() {
    this.simpleFade('.plus', '.plus_titre');
  }

  // -----------------------
  // CHIEN
  // -----------------------
  initChien() {
    if (!this.element.querySelector('.chien')) return;

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: '.chien',
        start: 'top 45%',
        end: '+=300',
        scrub: true,
      }
    });

    tl.to('.cercle_rouge_chien', { scale: 1 })
      .to('.carre_bleu_chien', { top: '5vh', opacity: 1 }, '<')
      .to('.triangle_jaune_chien', { top: '30vh', opacity: 1 }, '<')
      .to('.chien_titre', { paddingTop: 0, opacity: 1 })
      .to('.grid_image_chien_1, .grid_image_chien_2, .grid_image_chien_3, .grid_image_chien_4', {
        top: 0,
        opacity: 1,
        stagger: 0.1
      });
  }

  // -----------------------
  // INTERETS
  // -----------------------
  initInterets() {
    if (!this.element.querySelector('.interets')) return;

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: '.interets',
        start: 'top 50%',
        end: '60% 50%',
        scrub: true,
      }
    });

    [1, 2, 3].forEach(i => {
      tl.to(`.interets_cards_${i}`, { scale: 1 })
        .to(`.interets_cards_content_${i}`, { top: '100%', opacity: 1 });
    });
  }

  // -----------------------
  // MARCHE
  // -----------------------
  initMarche() {
    if (!this.element.querySelector('.marche')) return;

    gsap.timeline({
      scrollTrigger: {
        trigger: '.marche',
        start: '50% 50%',
        end: '+=400px',
        scrub: true,
        pin: true,
      }
    })
    .to('.souligne', { '--width-after-souligne': '0' })
    .to('.souligne', { '--width-after-souligne': '100%' });
  }

  // -----------------------
  // CONTEXTE
  // -----------------------
  initContexte() {
    if (!this.element.querySelector('.contexte')) return;

    gsap.timeline({
      scrollTrigger: {
        trigger: '.contexte',
        start: 'top 50%',
        end: '+=400',
        scrub: true,
      }
    })
    .to('.lignes_contexte', { height: '110%' })
    .to('.cercle_jaune_contexte', { opacity: 1 }, '<')
    .to('.contexte_contenu', { opacity: 1, marginTop: 100 }, '<');
  }

  // -----------------------
  // WICK - SIMPLE FADE
  // -----------------------
  initWick() {
    this.simpleFade('.wick', '.cine');
  }

  // -----------------------
  // CROQUIS
  // -----------------------
  initCroquis() {
    if (!this.element.querySelector('.croquis')) return;

    gsap.timeline({
      scrollTrigger: {
        trigger: '.croquis',
        start: '20% 65%',
        end: '40% 65%',
        scrub: true,
      }
    })
    .to('.croquis_content', { opacity: 1 })
    .to('h5', { marginTop: 40, marginBottom: 40 }, '<');
  }

  // -----------------------
  // RESULTATS - SIMPLE FADE
  // -----------------------
  initResultats() {
    this.simpleFade('.resultat', '.video');
  }

  // -----------------------
  // AMELIORATION - SIMPLE FADE
  // -----------------------
  initAmelioration() {
    this.simpleFade('.amelioration', '.amelioration_content');
  }

  // -----------------------
  // DISCUTE
  // -----------------------
  initDiscute() {
    if (!this.element.querySelector('.discute')) return;

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: '.discute',
        start: '10% 80%',
        end: '+=250px',
        scrub: true,
      }
    });

    [1, 2, 3, 4].forEach(i => {
      tl.to(`.cercle_discute_${i}`, { '--size': `${80 + i * 20}px` }, '<');
    });

    tl.to('.forme_discute_2', { top: '25%' }, '<')
      .to('.forme_discute_3', { top: '50%' }, '<')
      .to('.forme_discute_5', { top: '25%' }, '<')
      .to('.forme_discute_6', { top: '50%' }, '<');
  }
}